import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
    ActivityChartProps,
    ActivityChartState,
    ProviderSeriesData
} from '../utils/ActivityChart.types';
import { SupportedFitnessApp } from '../../../../core/api/FitnessActivityClient';

import {
    extractAvailableData,
    transformToProviderSeries,
    getActivityLabel,
    getProviderLabel,
    getProviderColor
} from '../utils/ActivityChart.utils';

type UseActivityChartStoreArgs = Pick<ActivityChartProps, 'data'> & {
    offsetMinutes?: number;
};

export function useActivityChartStore({
    data,
    offsetMinutes = 0
}: UseActivityChartStoreArgs) {
    const [state, setState] = useState<ActivityChartState>({
        loading: false,
        error: null,
        apiResponse: null,
        providerSeries: [],
        availableActivities: [],
        availableProviders: [],
        selectedActivities: [],
        selectedProviders: []
    });

    useEffect(() => {
        if (!data) {
            setState((prev) => ({
                ...prev,
                apiResponse: null,
                providerSeries: [],
                availableActivities: [],
                availableProviders: []
            }));
            return;
        }

        const { availableActivities, availableProviders } =
            extractAvailableData(data);
        const providerSeries = transformToProviderSeries(data, offsetMinutes);

        setState((prev) => {
            const nextSelectedActivities = prev.selectedActivities.filter((a) =>
                availableActivities.includes(a)
            );
            const nextSelectedProviders = prev.selectedProviders.filter((p) =>
                availableProviders.includes(p)
            );

            return {
                ...prev,
                apiResponse: data,
                providerSeries,
                availableActivities,
                availableProviders,
                selectedActivities:
                    nextSelectedActivities.length > 0
                        ? nextSelectedActivities
                        : availableActivities,
                selectedProviders:
                    nextSelectedProviders.length > 0
                        ? nextSelectedProviders
                        : availableProviders
            };
        });
    }, [data, offsetMinutes]);

    const setSelectedActivities = useCallback((activities: string[]) => {
        setState((prev) => ({ ...prev, selectedActivities: activities }));
    }, []);

    const setSelectedProviders = useCallback(
        (providers: SupportedFitnessApp[]) => {
            setState((prev) => ({ ...prev, selectedProviders: providers }));
        },
        []
    );

    const activityTypeGroups = useMemo(() => {
        const groups = new Map<string, ProviderSeriesData[]>();
        state.providerSeries.forEach((s) => {
            if (!groups.has(s.activityType)) groups.set(s.activityType, []);
            groups.get(s.activityType)!.push(s);
        });
        groups.forEach((g) =>
            g.sort((a, b) => a.provider.localeCompare(b.provider))
        );
        return groups;
    }, [state.providerSeries]);

    const chartSeries = useMemo(() => {
        return state.providerSeries
            .filter((ps) => {
                const activityMatch = state.selectedActivities.some(
                    (a) => ps.activityType === a
                );
                const providerMatch = state.selectedProviders.includes(
                    ps.provider
                );
                return activityMatch && providerMatch;
            })
            .map((ps) => {
                const activityGroup =
                    activityTypeGroups.get(ps.activityType) || [];
                const providerIndex = activityGroup.findIndex(
                    (s) => s.provider === ps.provider
                );
                const color = getProviderColor(ps.activityType, providerIndex);

                return {
                    label: `${getActivityLabel(ps.activityType)} - ${getProviderLabel(ps.provider)}`,
                    data: ps.data.map((d) => d.value),
                    curve: 'stepAfter' as const,
                    color,
                    showMark: false,
                    valueFormatter: (value: number | null) =>
                        value === null ? '' : value.toLocaleString()
                };
            });
    }, [
        state.providerSeries,
        state.selectedActivities,
        state.selectedProviders,
        activityTypeGroups
    ]);

    const xAxisData = useMemo<Date[]>(() => {
        if (state.providerSeries.length === 0) return [];
        return state.providerSeries[0].data.map((d) => d.time);
    }, [state.providerSeries]);

    const formatXAxisLabel = useCallback((date: Date) => {
        return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'UTC'
        });
    }, []);

    return {
        state,
        setSelectedActivities,
        setSelectedProviders,
        chartSeries,
        xAxisData,
        formatXAxisLabel
    };
}
